/**
 * PWCurrentRules.js
 * Pure business logic for Pregnant Women Current Vaccination scenario
 *
 * Phase 1: Extracted from vaccinationutils.js to create modular, testable logic
 * This contains ALL TD (Tetanus-Diphtheria) vaccine rules for PW Current tab
 */

import dayjs from 'dayjs';
import {
  VACCINE_NAMES,
  normalizeVaccineName,
  recordMatchesMaterial,
} from '../../vaccinationUtils/vaccineIdentity';

// ================= PURE BUSINESS LOGIC FUNCTIONS =================

/**
 * Calculate days from Last Menstrual Cycle (LMC) date to today
 * @param {string} lmcDate - LMC date in YYYY-MM-DD format
 * @returns {number} - Number of days from LMC to today
 */
export const calculateDaysFromLMC = (lmcDate) => {
  if (!lmcDate) return 0;

  try {
    const lmc = dayjs(lmcDate);
    const today = dayjs();
    return today.diff(lmc, 'day');
  } catch (error) {
    console.error('Error calculating days from LMC:', error);
    return 0;
  }
};

/**
 * Calculate days from a specific dose date to today
 * @param {string} doseDate - Dose date in ISO format
 * @returns {number} - Number of days from dose to today
 */
export const calculateDaysFromDose = (doseDate) => {
  if (!doseDate) return 0;

  try {
    const dose = dayjs(doseDate);
    const today = dayjs();
    return today.diff(dose, 'day');
  } catch (error) {
    console.error('Error calculating days from dose:', error);
    return 0;
  }
};

/**
 * Decrypt dose number from encrypted string
 * @param {string} encryptedDose - Encrypted dose string
 * @param {Function} decryptFunction - Decryption function
 * @returns {number} - Decrypted dose number
 */
export const decryptDoseNumber = (encryptedDose, decryptFunction) => {
  try {
    const decrypted = decryptFunction(encryptedDose);
    return parseInt(decrypted, 10);
  } catch (error) {
    console.error('Error decrypting dose:', error);
    return 0;
  }
};

/**
 * Check if vaccination record has a valid vaccination date
 * @param {Object} vaccinationRecord - The vaccination record
 * @returns {boolean} - True if record has valid vaccination_date
 */
export const hasValidVaccinationDate = (vaccinationRecord) => {
  return vaccinationRecord.hasOwnProperty('vaccination_date') &&
         vaccinationRecord.vaccination_date !== null &&
         vaccinationRecord.vaccination_date !== undefined &&
         vaccinationRecord.vaccination_date !== '';
};

/**
 * Calculate assumed vaccination date for TD vaccines when actual date is missing
 * @param {number} doseNumber - Dose number (1 for TD-1, 2 for TD-2)
 * @param {string} lmcDate - Last Menstrual Cycle date
 * @param {Object} td1Record - TD-1 record (for TD-2 calculation)
 * @returns {string} - Calculated date in ISO format
 */
export const calculateAssumedTDDate = (doseNumber, lmcDate, td1Record = null, doseFrequencies = []) => {
  if (!lmcDate) return null;

  try {
    const lmc = dayjs(lmcDate);
    const df0 = doseFrequencies[0] !== undefined ? doseFrequencies[0] : 28;
    const df1 = doseFrequencies[1] !== undefined ? doseFrequencies[1] : 28;

    if (doseNumber === 1) {
      // TD-1: LMC + doseFrequencies[0]
      return lmc.add(df0, 'day').toISOString();
    } else if (doseNumber === 2) {
      // TD-2: Check if TD-1 has actual date
      if (td1Record && hasValidVaccinationDate(td1Record)) {
        // TD-1 has date: TD-1 date + doseFrequencies[1]
        const td1Date = dayjs(td1Record.vaccination_date);
        return td1Date.add(df1, 'day').toISOString();
      } else {
        // TD-1 has no date: LMC + doseFrequencies[0] + doseFrequencies[1]
        return lmc.add(df0 + df1, 'day').toISOString();
      }
    }
  } catch (error) {
    console.error('Error calculating assumed TD date:', error);
  }

  return null;
};

/**
 * Get TD-1 and TD-2 vaccination records from administered vaccinations
 * Handles missing vaccination_date by calculating assumed dates
 * @param {Array} administeredVaccinations - Array of administered vaccine records
 * @param {string} vaccineName - Vaccine name (e.g., "Tetanus and Diphtheria(Td)")
 * @param {Function} decryptFunction - Function to decrypt dose numbers
 * @param {string} lmcDate - Last Menstrual Cycle date (for calculating missing dates)
 * @returns {Object} - Object with td1 and td2 records (with calculated dates if needed)
 */
export const getTDVaccinationRecords = (
  administeredVaccinations,
  vaccineName,
  decryptFunction,
  lmcDate = null,
  doseFrequencies = [],
  identityContext = null
) => {
  if (!administeredVaccinations || !Array.isArray(administeredVaccinations)) {
    return { td1: null, td2: null };
  }

  const { registry, matchOptions } = identityContext || {};
  const canonicalName = vaccineName || VACCINE_NAMES.TD_PW;

  let tdRecords = administeredVaccinations.filter((record) =>
    recordMatchesMaterial(
      record,
      canonicalName,
      decryptFunction,
      registry,
      matchOptions || {}
    )
  );

  if (tdRecords.length === 0 && !registry) {
    // Legacy name-only fallback when registry is unavailable
    const lowerTarget = canonicalName?.toLowerCase();
    tdRecords = administeredVaccinations.filter(
      (v) => v.material_name?.toLowerCase() === lowerTarget
    );
  }

  if (tdRecords.length === 0) {
    return { td1: null, td2: null };
  }

  let td1 = null;
  let td2 = null;

  // Process each TD record and calculate missing dates where needed
  tdRecords.forEach(record => {
    const doseNumber = decryptDoseNumber(record.dose, decryptFunction);

    if (doseNumber === 1) {
      td1 = { ...record };

      if (!hasValidVaccinationDate(td1) && lmcDate) {
        td1.vaccination_date = calculateAssumedTDDate(1, lmcDate, null, doseFrequencies);
        td1.isCalculatedDate = true;
      } else {
        td1.isCalculatedDate = false;
      }

    } else if (doseNumber === 2) {
      td2 = { ...record };

      if (!hasValidVaccinationDate(td2) && lmcDate) {
        td2.vaccination_date = calculateAssumedTDDate(2, lmcDate, td1, doseFrequencies);
        td2.isCalculatedDate = true;
      } else {
        td2.isCalculatedDate = false;
      }
    }
  });

  return { td1, td2 };
};

/**
 * Get TD eligibility status for a specific dose in Current Vaccination scenario
 * CORE BUSINESS LOGIC for PW Current vaccinations
 * @param {Object} vaccine - Vaccine object
 * @param {number} doseIndex - Index of the dose (0 for TD-1, 1 for TD-2)
 * @param {string} lmcDate - Last Menstrual Cycle date
 * @param {Object} tdRecords - Object containing td1 and td2 records
 * @returns {Object} - DoseConfig object with status, enabled, showRadio properties
 */
export const getTDCurrentDoseStatus = (vaccine, doseIndex, lmcDate, tdRecords) => {
  const daysFromLMC = calculateDaysFromLMC(lmcDate);
  const { td1, td2 } = tdRecords;
  const df0 = vaccine.doseFrequencies?.[0] !== undefined ? vaccine.doseFrequencies[0] : 28;
  const df1 = vaccine.doseFrequencies?.[1] !== undefined ? vaccine.doseFrequencies[1] : 28;

  // TD-1 (First dose)
  if (doseIndex === 0) {
    // If already administered, show the date (calculated or actual)
    if (td1 && td1.vaccination_date) {
      return {
        status: 'administered',
        date: dayjs(td1.vaccination_date).format('DD/MM/YYYY'),
        enabled: false,
        showRadio: false,
        isCalculated: td1.isCalculatedDate || false
      };
    }

    // If less than specific dose frequency gap from LMC, show "Upcoming"
    if (daysFromLMC < df0) {
      const eligibleDate = dayjs(lmcDate).add(df0, 'day').format('MMMM D, YYYY');
      return {
        status: 'Upcoming',
        enabled: false,
        showRadio: false,
        reason: `Upcoming ${eligibleDate}`,
        isCalculated: false
      };
    }

    // If eligible gap from LMC has passed, show "Eligible"
    return {
      status: 'Eligible',
      enabled: true,
      showRadio: true,
      isCalculated: false
    };
  }

  // TD-2 (Second dose)
  if (doseIndex === 1) {
    // If already administered, show the date (calculated or actual)
    if (td2 && td2.vaccination_date) {
      return {
        status: 'administered',
        date: dayjs(td2.vaccination_date).format('DD/MM/YYYY'),
        enabled: false,
        showRadio: false,
        isCalculated: td2.isCalculatedDate || false
      };
    }

    // TD-2 cannot be given without TD-1 being administered first
    if (!td1 || !td1.vaccination_date) {
      return {
        status: 'Upcoming',
        enabled: false,
        showRadio: false,
        reason: null,
        isCalculated: false
      };
    }

    // TD-1 is administered, now check if gap days have passed since TD-1
    const daysFromTD1 = calculateDaysFromDose(td1.vaccination_date);

    if (daysFromTD1 < df1) {
      const eligibleDate = dayjs(td1.vaccination_date).add(df1, 'day').format('MMMM D, YYYY');
      return {
        status: 'Upcoming',
        enabled: false,
        showRadio: false,
        reason: `Upcoming ${eligibleDate}`,
        isCalculated: false
      };
    }

    // All conditions met: TD-1 administered + gap days passed, show "Eligible"
    return {
      status: 'Eligible',
      enabled: true,
      showRadio: true,
      isCalculated: false
    };
  }

  return {
    status: 'Unknown',
    enabled: false,
    showRadio: false,
    isCalculated: false
  };
};

/**
 * Check if a vaccine is PW Td (Tetanus and Diphtheria(Td)).
 * Uses exact registry-backed identity when available; name fallback otherwise.
 */
export const isTdPwVaccine = (vaccine, identityContext = null) => {
  if (!vaccine) {
    return false;
  }

  const { registry, matchOptions, decryptFunction } = identityContext || {};
  if (registry && decryptFunction) {
    return recordMatchesMaterial(
      vaccine,
      VACCINE_NAMES.TD_PW,
      decryptFunction,
      registry,
      matchOptions || {}
    );
  }

  return normalizeVaccineName(vaccine.name) === normalizeVaccineName(VACCINE_NAMES.TD_PW);
};

/**
 * Check if a vaccine is TD Booster (Tetanus and Diphtheria(Td) Booster).
 * Separate material from PW Td — single dose, LMC + gap eligibility (same as TD-1).
 */
export const isTdBoosterVaccine = (vaccine, identityContext = null) => {
  if (!vaccine) {
    return false;
  }

  const { registry, matchOptions, decryptFunction } = identityContext || {};
  if (registry && decryptFunction) {
    return recordMatchesMaterial(
      vaccine,
      VACCINE_NAMES.TD_BOOSTER,
      decryptFunction,
      registry,
      matchOptions || {}
    );
  }

  return normalizeVaccineName(vaccine.name) === normalizeVaccineName(VACCINE_NAMES.TD_BOOSTER);
};

/**
 * Get administered TD Booster record (single-dose material).
 * Assumed date when missing: LMC + doseFrequencies[0] (same rule as TD-1).
 */
export const getTdBoosterVaccinationRecord = (
  administeredVaccinations,
  decryptFunction,
  lmcDate = null,
  doseFrequencies = [],
  identityContext = null
) => {
  if (!administeredVaccinations || !Array.isArray(administeredVaccinations)) {
    return null;
  }

  const { registry, matchOptions } = identityContext || {};
  const canonicalName = VACCINE_NAMES.TD_BOOSTER;

  let boosterRecords = administeredVaccinations.filter((record) =>
    recordMatchesMaterial(
      record,
      canonicalName,
      decryptFunction,
      registry,
      matchOptions || {}
    )
  );

  if (boosterRecords.length === 0 && !registry) {
    const lowerTarget = canonicalName?.toLowerCase();
    boosterRecords = administeredVaccinations.filter(
      (v) => v.material_name?.toLowerCase() === lowerTarget
    );
  }

  if (boosterRecords.length === 0) {
    return null;
  }

  const record = { ...boosterRecords[0] };

  if (!hasValidVaccinationDate(record) && lmcDate) {
    record.vaccination_date = calculateAssumedTDDate(1, lmcDate, null, doseFrequencies);
    record.isCalculatedDate = true;
  } else {
    record.isCalculatedDate = false;
  }

  return record;
};

/**
 * @deprecated Use isTdPwVaccine — kept for PW previous rules import compatibility.
 */
export const isTDVaccine = (vaccine, identityContext = null) =>
  isTdPwVaccine(vaccine, identityContext);

/**
 * MAIN ENTRY POINT: Get dose status for PW Current vaccination scenario
 * This is the unified interface that will be called by the UI components
 * @param {Object} vaccine - Vaccine object
 * @param {number} doseIndex - Index of the dose (0-based)
 * @param {Object} profileData - Profile data containing lmc_date and vaccination history
 * @param {Function} decryptFunction - Function to decrypt dose numbers
 * @returns {Object} - DoseConfig object for UI consumption
 */
export const getPWCurrentDoseStatus = (
  vaccine,
  doseIndex,
  profileData,
  decryptFunction,
  identityContext = null
) => {
  const identityCtx = { ...identityContext, decryptFunction };
  const { lmc_date, vaccination: administeredVaccinations } = profileData || {};

  // TD Booster: single dose, same eligibility as TD-1 (LMC + gap to today)
  if (isTdBoosterVaccine(vaccine, identityCtx)) {
    if (!lmc_date) {
      console.warn('No LMC date found for TD Booster eligibility');
      return {
        status: 'Unknown',
        enabled: false,
        showRadio: false,
        reason: 'LMC date not available',
        isCalculated: false
      };
    }

    const boosterRecord = getTdBoosterVaccinationRecord(
      administeredVaccinations || [],
      decryptFunction,
      lmc_date,
      vaccine.doseFrequencies,
      identityContext
    );
    const tdRecords = { td1: boosterRecord, td2: null };

    return getTDCurrentDoseStatus(vaccine, doseIndex, lmc_date, tdRecords);
  }

  // PW Td (TD-1 / TD-2 sequence)
  if (isTdPwVaccine(vaccine, identityCtx)) {
    if (!lmc_date) {
      console.warn('No LMC date found for TD vaccine eligibility');
      return {
        status: 'Unknown',
        enabled: false,
        showRadio: false,
        reason: 'LMC date not available',
        isCalculated: false
      };
    }

    const tdRecords = getTDVaccinationRecords(
      administeredVaccinations || [],
      vaccine.name,
      decryptFunction,
      lmc_date,
      vaccine.doseFrequencies,
      identityContext
    );

    return getTDCurrentDoseStatus(vaccine, doseIndex, lmc_date, tdRecords);
  }

  // Generic logic for other PW vaccines
  return {
    status: 'Eligible',
    enabled: true,
    showRadio: true,
    isCalculated: false
  };
};